/**
 * Avança o foco para o próximo campo editável (Enter = Tab do Delphi).
 *
 * Eventos sintéticos de Tab no `document` **não** movem o foco no browser.
 * Aqui percorremos o DOM e focamos o próximo input/select/textarea/combobox visível.
 */

function elementoVisivel(el: HTMLElement): boolean {
	if (el.getAttribute("aria-hidden") === "true") {
		return false;
	}

	const style = window.getComputedStyle(el);
	if (
		style.display === "none"
		|| style.visibility === "hidden"
		|| style.opacity === "0"
	) {
		return false;
	}

	// Fora da árvore visual (exceto em overlays/dialogs abertos)
	if (el.offsetParent === null) {
		const emDialog = el.closest(".p-dialog, .p-overlaypanel, [role='dialog']");
		if (!emDialog) {
			return false;
		}
	}

	return true;
}

function resolverContainer(ativo: HTMLElement): ParentNode {
	return (
		ativo.closest("#recebimento-conferencia-planilha-cega")
		?? ativo.closest(".p-dialog-content")
		?? ativo.closest("form")
		?? ativo.closest(".login-body")
		?? ativo.closest(".login-fields")
		?? document.body
	);
}

/**
 * Libera input desabilitado/readonly para receber foco (Delphi: Enter passa mesmo se “travado”).
 */
function liberarParaFoco(el: HTMLElement): void {
	if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
		el.disabled = false;
		el.removeAttribute("disabled");
		if ("readOnly" in el) {
			(el as HTMLInputElement).readOnly = false;
			el.removeAttribute("readonly");
		}
		el.tabIndex = 0;
	}
}

/**
 * Combobox do PrimeVue Dropdown (span/input com role=combobox).
 * Não é `<input>` no modo padrão — precisa entrar na cadeia Enter=Tab.
 */
function eComboboxFocavel(el: HTMLElement): boolean {
	if (el.getAttribute("role") !== "combobox") {
		return false;
	}
	if (el.getAttribute("aria-disabled") === "true") {
		return false;
	}
	if (el.closest(".p-disabled, .p-dropdown-disabled")) {
		return false;
	}
	// Dropdown desabilitado usa tabindex=-1
	if (el.tabIndex < 0) {
		return false;
	}
	return true;
}

/**
 * Campos de digitação / escolha na cadeia Enter (coletor).
 * Checkbox/radio ficam de fora — no login, "Desativar teclado" não deve
 * interceptar o caminho senha → empresa → Entrar.
 */
function eCandidatoCadeiaEnter(el: HTMLElement): boolean {
	if (!elementoVisivel(el)) {
		return false;
	}

	if (eComboboxFocavel(el)) {
		return true;
	}

	if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
		return true;
	}

	if (el instanceof HTMLInputElement) {
		const tipo = (el.type || "text").toLowerCase();
		// Só digitação — evita checkbox, radio, button, hidden, etc.
		if (
			tipo === "checkbox"
			|| tipo === "radio"
			|| tipo === "button"
			|| tipo === "submit"
			|| tipo === "reset"
			|| tipo === "file"
			|| tipo === "hidden"
			|| tipo === "image"
		) {
			return false;
		}
		// readonly+disabled calculados (ex.: totais) não entram
		if (el.readOnly && el.disabled) {
			return false;
		}
		return true;
	}

	return false;
}

/**
 * @param de Elemento de origem (default: `document.activeElement`)
 */
export function focarProximoCampoEditavel(de?: HTMLElement | null): boolean {
	const ativo = (de ?? document.activeElement) as HTMLElement | null;
	if (!ativo || ativo === document.body) {
		return false;
	}

	const container = resolverContainer(ativo);

	// input/select/textarea + combobox do Dropdown (PrimeVue)
	const seletor =
		"input:not([type=hidden]),"
		+ "select,"
		+ "textarea,"
		+ "[role='combobox']";

	const candidatos = Array.from(
		container.querySelectorAll<HTMLElement>(seletor),
	).filter(eCandidatoCadeiaEnter);

	let indice = candidatos.indexOf(ativo);
	if (indice < 0) {
		indice = candidatos.findIndex(
			el => ativo === el || ativo.contains(el) || el.contains(ativo),
		);
	}

	if (indice < 0) {
		return false;
	}

	// Avança pulando inputs que continuam sem poder focar (ex.: total calculado disabled+readonly)
	for (let i = indice + 1; i < candidatos.length; i++) {
		const proximo = candidatos[i];
		// Qtd Total Unid: sempre readonly/disabled — pula
		if (
			proximo instanceof HTMLInputElement
			&& (proximo.name === "QtdTotalUnidade" || proximo.id?.includes("total"))
			&& (proximo.readOnly || proximo.disabled)
		) {
			continue;
		}
		liberarParaFoco(proximo);
		proximo.focus();
		if (proximo instanceof HTMLInputElement) {
			try {
				proximo.select();
			}
			catch {
				/* ignore */
			}
		}
		if (document.activeElement === proximo) {
			return true;
		}
	}

	// Último campo: tenta o botão Conf / Confirmar / conferir / Entrar (login)
	const botoes = Array.from(
		container.querySelectorAll<HTMLButtonElement>("button:not([disabled])"),
	).filter(elementoVisivel);

	const conf = botoes.find((b) => {
		const t = (b.textContent ?? "").trim().toLowerCase();
		const id = (b.id ?? "").toLowerCase();
		return (
			t === "conf"
			|| t.startsWith("conf")
			|| t.includes("conferir")
			|| t === "entrar"
			|| id === "login-button"
		);
	});

	if (conf) {
		conf.focus();
		return true;
	}

	return false;
}
